import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckSquare, Megaphone, Flag, Users, BookOpen, ArrowRight } from "lucide-react";
import { Reveal, Tag, H2, GoldBtn } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import CursorGrid from "@/components/MagicBento/CursorGrid";
import FoldText from "@/components/FoldText/FoldText";
import TiltedCard from "@/components/TiltedCard/TiltedCard";

const proofs = [
  "Over 30 years in the insurance industry",
  "Former insurance agency owner",
  "Experience designing insurance programs",
  "Deep understanding of claim patterns and policyholder confusion",
  "AI-powered claim guidance",
  "Growing consumer advocacy community",
];

const actions = [
  { icon: Megaphone, title: "Tell Your Story", copy: "Share what happened with your claim." },
  { icon: Flag, title: "Report A Denied Or Lowballed Claim", copy: "Help Inssux identify patterns and build better consumer support." },
  { icon: Users, title: "Learn From Others", copy: "See common claim issues, red flags, and next-step guidance." },
  { icon: BookOpen, title: "Become More Informed", copy: "Use Jack and Inssux resources to understand the claims process more clearly." },
];

export const Trust = () => {
  const isMobile = useMobileDetection();

  return (
  <section id="trust" data-testid="trust-section" className="relative bg-[#0A0A0C] py-24 md:py-32 overflow-hidden">
    <CursorGrid
      disableAnimations={isMobile}
      highlightColor="184, 133, 245"
      gridSize={60}
      radius={700}
      intensity={0.05}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: "radial-gradient(700px circle at 20% 50%, rgba(138,43,226,0.12), transparent 60%)" }}
    />
    <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
      <Reveal>
        <Tag testId="trust-tag">Trust</Tag>
        <H2>
          <FoldText
            text="Built by someone who knows how insurance works."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />
        </H2>
        <p className="mt-6 text-[#A1A1AA] font-light leading-relaxed">
          Inssux was built from more than 30 years of real insurance industry experience. That
          matters because most people only see the insurance process from the outside. Inssux was
          created to help people better understand the inside language, patterns, and decisions
          that can affect their claim.
        </p>
        <p className="mt-6 font-serif-jack italic text-lg text-[#9D4EDD]">
          Inssux does not guarantee claim results. It helps people understand their options,
          prepare better questions, and make more informed decisions.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <ul className="mt-10 grid sm:grid-cols-2 gap-4">
          {proofs.map((p, i) => (
            <li key={p} data-testid={`proof-point-${i}`} className="flex items-start gap-3.5 bg-[#0A0A0C] border border-white/10 p-5">
              <CheckSquare size={16} className="mt-0.5 shrink-0 text-[#8A2BE2]" />
              <span className="text-sm text-white/85 font-light leading-relaxed">{p}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      </div>
      <Reveal delay={0.2} className="lg:col-span-5">
        <div className="relative max-w-[400px] mx-auto lg:ml-auto">
          <button
            type="button"
            onClick={() => window.AIWidget?.ask()}
            aria-label="Ask Jack"
            className="block w-full cursor-pointer"
          >
            <TiltedCard
              imageSrc="/jack-trustwall.jpg"
              altText="Jack in front of his credentials wall"
              testId="trust-visual"
              containerHeight="480px"
              containerWidth="100%"
              imageHeight="480px"
              imageWidth="100%"
              scaleOnHover={1.05}
              rotateAmplitude={8}
              showMobileWarning={false}
              showTooltip={false}
              className="tilted-card-figure--square"
              innerClassName="overflow-hidden border border-[#FFD166]/40 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] after:absolute after:inset-0 after:content-[''] after:pointer-events-none after:bg-[linear-gradient(to_bottom,transparent_60%,rgba(10,10,12,0.85))]"
              imgClassName="object-[50%_20%]"
              overlayContent={
                <p className="font-mono-file text-[9px] tracking-[0.25em] text-[#FFD166]">
                  30+ YEARS INSIDE THE INDUSTRY
                </p>
              }
              displayOverlayContent
              overlayClassName="w-full h-full flex flex-col justify-end p-4"
            />
          </button>
          <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[#8A2BE2]/60" />
          <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[#8A2BE2]/60" />
        </div>
      </Reveal>
    </div>
  </section>
  );
};

export const Community = () => {
  const gridRef = useRef(null);
  const sectionRef = useRef(null);
  const isMobile = useMobileDetection();
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
  <section ref={sectionRef} id="community" data-testid="community-section" className="relative bg-black py-24 md:py-32 overflow-hidden">
    <GlobalSpotlight
      gridRef={gridRef}
      disableAnimations={isMobile}
      glowColor="255, 209, 102"
      spotlightRadius={280}
    />
    <motion.img
      src="/jack-community.jpg"
      alt=""
      style={{ y }}
      className="absolute left-0 w-full h-[130%] -top-[15%] object-cover opacity-45 object-[55%_center] lg:object-center"
    />
    <div
      className="absolute inset-0"
      style={{ background: "linear-gradient(to bottom, rgba(5,5,8,0.92) 5%, rgba(5,5,8,0.55) 45%, rgba(5,5,8,0.9) 95%)" }}
    />
    <div className="relative max-w-[1280px] mx-auto px-6 lg:px-14">
      <Reveal>
        <Tag testId="community-tag">Community</Tag>
        <H2 className="max-w-2xl">
          <FoldText
            text="You are not the only one getting the runaround."
            splitBy="word"
            trigger="scroll"
            fontSize="inherit"
            fontWeight="inherit"
            color="inherit"
          />
        </H2>
        <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
          Thousands of people deal with delayed, denied, confusing, or underpaid insurance claims
          every day. Inssux is building more than a tool. It is building a community where people
          can report what happened, learn from common patterns, and stop feeling like they are
          fighting alone.
        </p>
      </Reveal>
      <div ref={gridRef} className="bento-section mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {actions.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.09}>
            <ParticleCard
              testId={`community-action-${i}`}
              disableAnimations={isMobile}
              glowColor="255, 209, 102"
              className="magic-bento-card magic-bento-card--border-glow h-full bg-black/60 backdrop-blur-sm border border-white/15 p-7 hover:border-[#FFD166]/50 transition-colors duration-300"
            >
              <a.icon size={20} className="text-[#9D4EDD]" />
              <h3 className="mt-3 font-display text-base tracking-wide text-white">{a.title}</h3>
              <p className="mt-2 text-sm text-[#A1A1AA] font-light leading-relaxed">{a.copy}</p>
            </ParticleCard>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.35}>
        <div className="mt-11">
          <GoldBtn testId="community-cta">
            REPORT MY CLAIM
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </GoldBtn>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
